import Boundary from './Boundary.js';
import NPCStateMachine from './NPCStateMachine.js';
import Sprite from '../ui/Sprite.js';
import { imageStore } from '../images.js';
import { getOSM } from '../OverworldAnimation.js';
import ExclamationMark from '../ui/ExclamationMark.js';
import Pokemon from './Pokemon.js';
import { READ_TEXT_SOURCE } from '../constants.js';
import NPCShadow from '../ui/NPCShadow.js';

export default class NPC extends Boundary {
    serialize() {
        console.log(`Serializing ${this.constructor.name}`);
        return {
            name: this.name,
            identifier: this.identifier,
            position: this.position,
            type: this.type,
            direction: this.direction,
            isDefeated: this.isDefeated,
            visible: this.visible,
            preBattleText: this.preBattleText,
            postBattleText: this.postBattleText,
            text: this.text,
            viewRange: this.viewRange,
            party: this.party.map((pokemon) => pokemon.serialize()),
            _canInteract: this._canInteract,
            useFresh: this.useFresh,

            // State machine and runtime state
            shouldShowExclamationMark: this.shouldShowExclamationMark,
            isInteracting: this.isInteracting,
            stateMachine: null, // Create a new one upon deserialization

            // Movement state
            xModifier: this.xModifier,
            yModifier: this.yModifier,
            movementSpeed: this.movementSpeed,

            // Step tracking state
            steps: this.steps,
            forcedStepCount: this.forcedStepCount,
            positionHistory: this.positionHistory,

            // Collision state
            collisions: {
                up: false,
                right: false,
                down: false,
                left: false,
            },
        };
    }

    static async deserialize(data) {
        const newType = data.type === 'lad' ? 'youngster' : data.type;
        const npc = new NPC({
            name: data.name,
            identifier: data.identifier,
            position: data.position,
            type: newType,
            direction: data.direction,
            preBattleText: data.preBattleText,
            postBattleText: data.postBattleText,
            text: data.text,
            viewRange: data.viewRange,
            isDefeated: data.isDefeated,
            _canInteract: data._canInteract,
            useFresh: data.useFresh !== undefined ? data.useFresh : true,
            party: [], // We'll set this after deserializing and resolving references
        });

        // Deserialize Pokemon party
        npc.party = await Promise.all(data.party.map((pokemonData) => Pokemon.deserialize(pokemonData)));

        // Resolve trainer references for NPC's Pokemon
        npc.party.forEach((pokemon) => {
            // For NPCs, they are usually both original and current trainer
            if (pokemon._originalTrainerId === npc.name || pokemon._currentTrainerId === npc.name) {
                pokemon.originalTrainer = npc;
                pokemon.currentTrainer = npc;
            }

            // Clean up temporary ID properties
            delete pokemon._originalTrainerId;
            delete pokemon._currentTrainerId;
        });

        // Restore runtime state
        npc.visible = data.visible;
        npc.shouldShowExclamationMark = data.shouldShowExclamationMark;
        npc.isInteracting = data.isInteracting;
        npc.xModifier = data.xModifier || 0;
        npc.yModifier = data.yModifier || 0;
        npc.movementSpeed = data.movementSpeed || 1;
        npc.steps = data.steps || 0;
        npc.forcedStepCount = data.forcedStepCount || 0;
        npc.positionHistory = data.positionHistory || [
            { x: npc.position.x, y: npc.position.y, z: npc.position.z },
        ];
        npc.collisions = data.collisions || {
            up: false,
            right: false,
            down: false,
            left: false,
        };

        // Restore state machine
        if (data.stateMachine) {
            npc.stateMachine = NPCStateMachine.deserialize(data.stateMachine, npc);
        } else {
            npc.stateMachine = new NPCStateMachine({ npc: npc });
        }

        return npc;
    }

    constructor({
        name,
        identifier,
        position,
        type,
        party = [],
        preBattleText = '',
        postBattleText = '',
        text = 'Hello!',
        viewRange = 0,
        OSM,
        isDefeated = true,
        _canInteract = true,
        direction = 'down',
        movementPattern,
        useFresh = true,
    }) {
        super({ position });
        this.name = name;
        this.identifier = identifier;
        this.position = position;
        this.type = type;
        this.direction = direction;
        this.xModifier = 0;
        this.yModifier = 0;
        this.stateMachine = new NPCStateMachine({ npc: this });
        this.OSM = OSM;
        this.isInteracting = false;
        this.movementSpeed = 1;
        this.shouldShowExclamationMark = false;
        this.isDefeated = isDefeated;
        this.movementPattern = movementPattern ?? 'idle';

        this.shadow = new NPCShadow({});

        this.adjacentObjects = {
            up: null,
            right: null,
            down: null,
            left: null,
            center: null,
        };

        this.collisions = {
            up: false,
            right: false,
            down: false,
            left: false,
        };

        this.exclamationMark = new ExclamationMark({ npc: this });

        this.images = {
            up: imageStore.get(`img/npc/${this.type}/up.png`),
            right: imageStore.get(`img/npc/${this.type}/right.png`),
            down: imageStore.get(`img/npc/${this.type}/down.png`),
            left: imageStore.get(`img/npc/${this.type}/left.png`),
        };

        this.sprite = new Sprite({
            position: this.position,
            image: this.images.down, // Start with the down image
            frames: { max: 4 },
            moving: false,
            hold: 16,
            visible: true,
        });

        this.visible = true;
        this.preBattleText = preBattleText;
        this.postBattleText = postBattleText;
        this.text = text;
        this.viewRange = parseInt(viewRange);
        this.party = party;
        this._canInteract = _canInteract;
        this.useFresh = useFresh;
        this.interactionPriority = 10;

        // Step tracking properties (similar to Player)
        this.steps = 0;
        this.forcedStepCount = 0;
        this.positionHistory = [];
    }

    setType(type) {
        this.type = type;
        this.images = {
            up: imageStore.get(`img/npc/${type}/up.png`),
            right: imageStore.get(`img/npc/${type}/right.png`),
            down: imageStore.get(`img/npc/${type}/down.png`),
            left: imageStore.get(`img/npc/${type}/left.png`),
        };
    }

    canInteract() {
        return this._canInteract;
    }

    interact() {
        getOSM().stateQueue.push(READ_TEXT_SOURCE);
        this.isInteracting = true;
    }

    endInteraction() {
        this.isInteracting = false;
    }

    spritePosition() {
        const position = this.effectivePosition();
        position.y -= 8;
        return position;
    }

    alignedToGrid() {
        return this.position.x % 16 === 0 && this.position.y % 16 === 0;
    }

    showExclamationMark() {
        this.shouldShowExclamationMark = true;
        this.exclamationMark.sprite.frames.val = 0;
        this.exclamationMark.updatePosition();
        this.exclamationMark.visible = true;
    }

    hideExclamationMark() {
        this.shouldShowExclamationMark = false;
        this.exclamationMark.visible = false;
        this.exclamationMark.sprite.moving = false;
        this.exclamationMark.sprite.frames.val = 0;
    }

    distanceToPlayer() {
        const playerPosition = getOSM().player.position;
        switch (this.direction) {
            case 'up':
                return Math.floor(Math.abs(this.position.y - playerPosition.y) / 16);
            case 'right':
                return Math.floor(Math.abs(this.position.x - playerPosition.x) / 16);
            case 'down':
                return Math.floor(Math.abs(this.position.y - playerPosition.y) / 16);
            case 'left':
                return Math.floor(Math.abs(this.position.x - playerPosition.x) / 16);
        }
    }

    draw() {
        if (this.shadow.npc === null || this.shadow.npc === undefined) {
            this.shadow.npc = this;
        }
        if (this.shouldShowExclamationMark) {
            this.exclamationMark.visible = true;
            this.exclamationMark.sprite.moving = true;
            if (this.exclamationMark.sprite.frames.val === 23) {
                this.shouldShowExclamationMark = false;
            }
        } else {
            this.exclamationMark.visible = false;
            this.exclamationMark.sprite.moving = false;
        }

        this.stateMachine.update();

        if (!this.visible) return;

        switch (this.direction) {
            case 'up':
                this.sprite.setImage(this.images.up);
                break;
            case 'right':
                this.sprite.setImage(this.images.right);
                break;
            case 'down':
                this.sprite.setImage(this.images.down);
                break;
            case 'left':
                this.sprite.setImage(this.images.left);
                break;
        }

        this.sprite.position = this.spritePosition();
        this.shadow.draw();
        this.sprite.draw();
    }

    // Movement Patterns
    faceRandomDirection() {
        if (Math.random() < 0.25) {
            this.direction = ['up', 'left', 'down', 'right'][Math.floor(Math.random() * 4)];
        }
    }

    idle() {}

    step() {
        if (this.positionHistory.length === 0) {
            this.positionHistory.push({
                x: this.position.x,
                y: this.position.y,
                z: this.position.z,
            });
            this.steps += 1;
            this.forcedStepCount = Math.max(this.forcedStepCount - 1, 0);
            return;
        }

        if (
            this.position.x === this.positionHistory[0].x &&
            this.position.y === this.positionHistory[0].y &&
            this.position.z === this.positionHistory[0].z
        ) {
            return;
        }

        this.positionHistory.unshift({
            x: this.position.x,
            y: this.position.y,
            z: this.position.z,
        });
        this.steps += 1;
        this.forcedStepCount = Math.max(this.forcedStepCount - 1, 0);
        if (this.positionHistory.length >= 10) {
            this.positionHistory.pop();
        }
    }
}
